/**
 * Playing the graph (the Play window): an environment that says how it is played (`play` in
 * its manifest: the node kinds played and the link kinds leading on) is walked one node at a
 * time, its page (or its document) shown and its ways on as choices, like reading a branching story or clicking
 * through a prototype. The way back is kept; changing environment starts again. The story can
 * be exported as one HTML file that plays by itself (`shared/story.ts`).
 */
import { createEffect, createMemo, createSignal, on, type Accessor } from "solid-js";
import type { PlayDef } from "@protocol/PlayDef";
import type { GraphMirror } from "../client/graph";
import { downloadText } from "../shared/download";
import { storyHtml, storyOf, type Passage, type Story } from "../shared/story";
import type { Documents } from "./documents";
import type { Environments } from "./environments";

/** The play service. */
export interface Play {
  /** How the current environment is played (`null`: it is not). */
  def: Accessor<PlayDef | null>;
  /** The graph shown, as a story (`null`: not played here). */
  story: Accessor<Story | null>;
  /** The passage being read (`null`: not started, or it is gone). */
  at: Accessor<Passage | null>;
  /** Whether there is a passage to go back to. */
  canBack: Accessor<boolean>;
  /** Starts at passage `id` (by default the story's first start). */
  start(id?: string): void;
  /** Takes a choice: goes to passage `to`, keeping the way back. */
  choose(to: string): void;
  /** Goes back to the passage before. */
  back(): void;
  /** Leaves the story (back to the list of starts). */
  stop(): void;
  /** Downloads the story as one HTML file that plays by itself; `false` when there is nothing to play. */
  exportFile(): boolean;
}

/** What the service needs. */
export interface PlayDeps {
  graph: Pick<GraphMirror, "state">;
  environments: Pick<Environments, "current">;
  /** The documents: a scene's text is its document's when it has one. */
  documents?: Pick<Documents, "textOf">;
  /** The story's title (the project's name). */
  title: () => string;
}

/** A file name made of `title`: letters, digits and dashes. */
export function fileNameOf(title: string): string {
  return `${title.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, "-").replace(/^-+|-+$/g, "") || "story"}.html`;
}

/** Starts the play service. */
export function createPlay({ graph, environments, documents, title }: PlayDeps): Play {
  const [at, setAt] = createSignal<string | null>(null);
  const [trail, setTrail] = createSignal<string[]>([]);
  const def = createMemo(() => environments.current()?.play ?? null);
  const story = createMemo(() => {
    const play = def();
    return play ? storyOf(graph.state.graph, play, (n) => documents?.textOf(n) ?? null) : null;
  });
  const stop = () => {
    setAt(null);
    setTrail([]);
  };
  // Another environment, another story.
  createEffect(on(() => environments.current()?.id, stop, { defer: true }));
  return {
    def,
    story,
    at: createMemo(() => {
      const id = at();
      return id ? (story()?.passages.get(id) ?? null) : null;
    }),
    canBack: () => trail().length > 0,
    start(id) {
      const first = id ?? story()?.starts[0];
      if (!first) return;
      setTrail([]);
      setAt(first);
    },
    choose(to) {
      const from = at();
      if (from) setTrail((t) => [...t, from]);
      setAt(to);
    },
    back() {
      const t = trail();
      if (!t.length) return;
      setAt(t[t.length - 1]!);
      setTrail(t.slice(0, -1));
    },
    stop,
    exportFile() {
      const s = story();
      if (!s || !s.passages.size) return false;
      const name = title() || "Story";
      downloadText(fileNameOf(name), storyHtml(s, name, at() ?? s.starts[0]), "text/html");
      return true;
    },
  };
}
